import {
  Box,
  buttonClasses,
  Divider,
  IconButton,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { TuneOutlined } from '@mui/icons-material';
import { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Brand_Enum, Business_Profile_Enum, Contract_Status_Enum } from '@kheops/graphql';
import ListIcon from '@mui/icons-material/List';
import GridViewIcon from '@mui/icons-material/GridView';
import SearchIcon from '@mui/icons-material/Search';
import { RoutePaths } from '../routes/AppRoutes';
import { useCompanyByIdQuery } from '../queries/__generated__/companyById.generated';
import { PageWrapper } from '../common/components/PageWrapper';
import { currentBuyingCompanyIdAtom } from '../basket/state/state';
import { contractAtom, currentContextAtom, userAtom } from '../state';
import FileUtils from '../common/utils/file.utils';
import SizedImage from '../common/components/SizedImage';
import CompanyLogo from '../common/components/CompanyLogo';
import ImageDarkOverlay from '../common/components/ImageDarkOverlay';
import SearchUtils from '../common/utils/search.utils';
import { UserDeliveryZone } from '../common/models/user.models';
import TopAppBar from '../common/components/TopAppBar';
import { getCompanyShortName, isRichTextEmpty } from '../common/utils/common.utils';
import BasketButton from '../basket/BasketButton';
import { companyPageDisplayModeAtom, DisplayMode } from '../search/state/state';
import TrustedSupplierChip from '../common/components/TrustedSupplierChip';
import useRelevantSocialTags from '../social-proof/hooks/useRelevantSocialTags';
import SocialTagCard from '../social-proof/SocialTagCard';
import ProductRecommendations from '../products/recommendations/ProductRecommendations';
import WishlistSupplierButton from '../wishlist/WishlistSupplierButton';
import ContactBox from './companyInfo/ContactBox';
import CompanyContactInfoBox from './CompanyContactInfoBox';
import CompanyUsers from './CompanyUsers';
import ExportProductsButton from './companyInfo/ExportProductsButton';
import CompanyDescription from './CompanyDescription';
import { isFilterDrawerOpenAtom, isWaitingForSubCatalogsSyncAtom, refreshCompanyProductListAtom } from './state';
import TrustedSupplierFloatingCard from './TrustedSupplierFloatingCard';
import { UnsignedCompanyInfoCard } from './companyInfo/UnsignedCompanyInfoCard';
import CompanyServicesInfo from './companyInfo/CompanyServicesInfo';
import CompanyContacts from './CompanyContacts';
import ContractInfoCards from './ContractInfoCards';
import { CompanyInfoCard } from './companyInfo/CompanyInfoCard';
import CompanyProductSearch from './CompanyProductSearch';

const BRANDS_ALLOWED_TO_EXPORT_PRODUCTS = [
  Brand_Enum.CarrefourMarket,
  Brand_Enum.CarrefourHyper,
  Brand_Enum.SuperU,
  Brand_Enum.HyperU,
  Brand_Enum.UExpress,
  Brand_Enum.IntermarcheHyper,
  Brand_Enum.IntermarcheSuper,
  Brand_Enum.IntermarcheExpress,
  Brand_Enum.IntermarcheContact,
  Brand_Enum.Spar,
  Brand_Enum.Utile,
  Brand_Enum.Franprix,
  Brand_Enum.Netto,
];

export default function CompanyPage(): React.JSX.Element {
  const searchInputRef = useRef<HTMLInputElement>(null);
  const theme = useTheme();
  const { t } = useTranslation(['contracts', 'common', 'company']);
  const context = useAtomValue(currentContextAtom);
  const { id: userId } = useAtomValue(userAtom);
  const { companyId } = useParams();
  const setCurrentBuyingCompanyId = useSetAtom(currentBuyingCompanyIdAtom);
  const isBuyer = context.realm === Business_Profile_Enum.Buyer;
  const [buyingCompanyId, supplyingCompanyId] = isBuyer
    ? [context.companyId, companyId!]
    : [companyId!, context.companyId];
  const companyContractAtom = useMemo(() => {
    return contractAtom(buyingCompanyId!, supplyingCompanyId);
  }, [buyingCompanyId, supplyingCompanyId]);
  const contract = useAtomValue(companyContractAtom);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isSmallerThanLg = useMediaQuery(theme.breakpoints.down('lg'));
  const [displayMode, setDisplayMode] = useAtom(companyPageDisplayModeAtom);
  const [hasScrolled, setHasScrolled] = useState(false);
  const setDrawerOpen = useSetAtom(isFilterDrawerOpenAtom);
  const setRefresh = useSetAtom(refreshCompanyProductListAtom);
  const setIsWaitingForSubcatalogsSync = useSetAtom(isWaitingForSubCatalogsSyncAtom);
  const location = useLocation();

  const [tabIndex, setTabIndex] = useState(0);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number): void => {
    setTabIndex(newValue);
  };

  useEffect(() => {
    if (isSmallerThanLg && displayMode === DisplayMode.LIST) {
      setDisplayMode(DisplayMode.GRID);
    }
  }, [isSmallerThanLg]);

  useEffect(() => {
    const handleScroll = (): void => {
      setHasScrolled(window.scrollY > 0);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTopAndFocusSearch = (): void => {
    searchInputRef.current!.scrollIntoView({ block: 'center', inline: 'nearest' });
    searchInputRef.current?.focus();
  };

  const companyQuery = useCompanyByIdQuery({
    variables: {
      companyId: companyId!,
    },
    skip: !buyingCompanyId || !supplyingCompanyId || !userId,
  });
  const company = companyQuery.data?.company_by_pk;
  const socialTags = useRelevantSocialTags((company?.social_tags || []), { rules: { 1: 1, 2: 1 } });

  const productSearchCompanyId = useMemo(() => {
    return isBuyer ? companyId! : context.companyId;
  }, [company]);

  const productListTitle = useMemo((): string => {
    let title = '';

    if (context) {
      if (isBuyer) {
        title = t(contract?.status === Contract_Status_Enum.Signed ? 'contracts:place_an_order_to_this_supplier' : 'common:product_other');
      } else {
        title = t('contracts:suggest_an_order_to_this_buyer');
      }
    }

    return title;
  }, [contract]);

  const displayProducts = useMemo((): boolean => {
    return company?.business_profile === Business_Profile_Enum.Supplier || contract?.status === Contract_Status_Enum.Signed;
  }, [contract, company]);

  const displayCompanyContacts = useMemo((): boolean => {
    return !isBuyer && contract?.status !== Contract_Status_Enum.Signed;
  }, [contract, location.state]);

  const backTo = isBuyer
    ? RoutePaths.DISCOVERY_SUPPLIERS
    : RoutePaths.DISCOVERY_BUYERS;

  const canDeliver = useMemo(() => {
    if (company) {
      if (isBuyer) {
        return SearchUtils.canItDeliverAnArea(company?.delivery_zones as UserDeliveryZone[], context.companyAddress.administrativeAreaLevel2!);
      }

      return SearchUtils.canItDeliverAnArea(context.deliveryZones, company.addresses[0].administrativeAreaLevel2!);
    }
  }, [company]);

  useEffect(() => {
    if (!isBuyer && companyId) {
      setCurrentBuyingCompanyId(companyId);
    } else {
      setCurrentBuyingCompanyId(context.companyId);
    }
  }, [companyId]);

  useEffect(() => {
    if (location.state?.newCustomPriceListId) {
      setTabIndex(1);
      return;
    }

    if (location.state?.newSubCatalogId) {
      setTabIndex(1);
      setIsWaitingForSubcatalogsSync(true);
      const timeout = setTimeout(
        () => {
          setRefresh(true);
          setIsWaitingForSubcatalogsSync(false);
        },
        4000, // We need to wait for the indexation of packagings records. 4s corresponds to a high waiting time for a company with over 200 products.
      );

      return () => {
        clearTimeout(timeout);
      };
    }

    if (location.state?.tabIndex) {
      setTabIndex(location.state.tabIndex);
    }
  }, []);

  const switchGridList = useMemo((): ReactElement => {
    return (
      <ToggleButtonGroup
        onChange={(_, newMode) => {
          if (newMode !== null) {
            setDisplayMode(newMode);
          }
        }}
        value={displayMode}
        exclusive
        sx={{
          display: {
            xs: 'none',
            lg: 'flex',
          },
          width: 144,
        }}
      >
        <ToggleButton value={DisplayMode.GRID}>
          <GridViewIcon sx={{ width: 18, height: 18 }} />
        </ToggleButton>
        <ToggleButton value={DisplayMode.LIST}>
          <ListIcon sx={{ width: 18, height: 18 }} />
        </ToggleButton>
      </ToggleButtonGroup>
    );
  }, [displayMode]);

  return (
    <>
      {contract && (
        <TrustedSupplierFloatingCard
          contract={contract}
          onSnackbarActionClick={() => setTabIndex(1)}
        />
      )}
      {!!company && (
        <TopAppBar
          configuration="small"
          backTo={backTo}
          title={getCompanyShortName(company.tradeName, company.business_profile)}
          trailingAction={(
            <>
              {contract?.status === Contract_Status_Enum.Signed && (tabIndex !== 0 || !hasScrolled) && (
                <>
                  <ContactBox contract={contract} contactCompanyId={company.id} actionButtonDisplay="icon" />
                  <BasketButton />
                </>
              )}
              {tabIndex === 0 && hasScrolled && (
                <>
                  <IconButton
                    size="large"
                    sx={{
                      color: 'onSurfaceVariant.main',
                    }}
                    onClick={scrollToTopAndFocusSearch}
                  >
                    <SearchIcon />
                  </IconButton>
                  <IconButton
                    size="large"
                    sx={{
                      color: 'onSurfaceVariant.main',
                    }}
                    onClick={() => setDrawerOpen(true)}
                  >
                    <TuneOutlined />
                  </IconButton>
                </>
              )}
            </>
          )}
        />
      )}
      {!!company && isSmallerThanLg && (
        <Box sx={{ mx: 1.5, mt: 0.5 }}>
          <SizedImage
            src={FileUtils.BuildCompanyImageUrl({
              photo: company.photos ? company.photos[0]?.photo : undefined,
              type: company.business_profile,
              size: { fit: 'fill' },
            })}
            alt={t('common:company')}
            width="100%"
            borderRadius="12px"
            sx={{
              mr: 2,
              '& img:not([class^="Kheops-companyLogo"]):not([class*=" Kheops-companyLogo"])': {
                aspectRatio: '3/1',
                objectFit: 'cover',
                width: '100%',
              },
            }}
            overlay={(
              <>
                {company.brand && (
                  <CompanyLogo
                    brand={company.brand}
                    size={isMobile ? 'medium' : 'large'}
                    variant="square"
                    sx={{
                      position: 'absolute',
                      bottom: {
                        xs: 12,
                        sm: 16,
                        md: 16,
                      },
                      left: {
                        xs: 12,
                        sm: 16,
                        md: 16,
                      },
                      width: {
                        xs: 48,
                        sm: 72,
                        md: 72,
                      },
                    }}
                  />
                )}
                {isBuyer && !canDeliver && contract?.status !== Contract_Status_Enum.Signed && (
                  <ImageDarkOverlay label={t('discovery:dont_deliver_in_your_area')} />
                )}
                {isBuyer && contract?.is_trusted_supplier && (
                  <TrustedSupplierChip
                    sx={{
                      position: 'absolute',
                      left: 12,
                      bottom: 12,
                      zIndex: 10,
                    }}
                  />
                )}
                {contract?.status !== Contract_Status_Enum.Signed && (
                  <Box
                    sx={{
                      position: 'absolute',
                      left: { xs: 8, sm: 36 },
                      bottom: { xs: 8, sm: 24 },
                      zIndex: 5,
                    }}
                  >
                    <WishlistSupplierButton supplierId={company.id} />
                  </Box>
                )}
              </>
            )}
          />
        </Box>
      )}
      <PageWrapper sx={{ pt: { xs: 0, lg: 2 } }}>
        {!!company && (
          <>
            {contract?.status === Contract_Status_Enum.Signed ? (
              <CompanyInfoCard company={company} contract={contract} />
            ) : (
              <UnsignedCompanyInfoCard company={company} contract={contract} />
            )}
            {contract?.status === Contract_Status_Enum.Signed && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mb: tabIndex === 0 ? 1.5 : 3,
                  justifyContent: 'space-between',
                }}
              >
                <Tabs
                  value={tabIndex}
                  onChange={handleTabChange}
                  variant="scrollable"
                  scrollButtons="auto"
                >
                  <Tab label={t(isBuyer ? 'basket:order' : 'basket:suggest')} />
                  <Tab label={t('common:contract')} />
                  {isBuyer && (
                    <Tab label={t('common:about')} />
                  )}
                </Tabs>
                {tabIndex === 0 && displayProducts && contract && (
                  switchGridList
                )}
              </Box>
            )}
            {contract?.status === Contract_Status_Enum.Signed && tabIndex === 0 && displayProducts && contract && (
              <>
                <CompanyProductSearch
                  contextCompanyId={companyId!}
                  companyId={productSearchCompanyId}
                  contract={contract}
                  searchInputRef={searchInputRef}
                />
                {isBuyer && <ProductRecommendations supplierId={companyId} />}
              </>
            )}
            {tabIndex === 1 && contract && (
              <ContractInfoCards contract={contract} company={company} />
            )}
            {tabIndex === 2 && (
              <Box
                sx={{
                  mx: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                {!!socialTags.length && (
                  <>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                      }}
                    >
                      <Typography variant="titleMedium">
                        {t('company:supplier_stats')}
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 1,
                          flexDirection: { xs: 'column', md: 'row' },
                        }}
                      >
                        {socialTags.map((tag) => (
                          <SocialTagCard key={tag.type} tag={tag} entity="SUPPLIER" />
                        ))}
                      </Box>
                    </Box>
                    <Divider />
                  </>
                )}
                <Typography variant="titleMedium">
                  {t('common:Description')}
                </Typography>
                {!!company.description && !isRichTextEmpty(company.description) ? (<CompanyDescription company={company} />) : '-'}
                <Divider />

                <CompanyServicesInfo services={company} />
                <Divider />

                {BRANDS_ALLOWED_TO_EXPORT_PRODUCTS.includes(context.companyBrand as Brand_Enum) && (
                  <>
                    <Typography variant="titleMedium">
                      {t('common:download')}
                    </Typography>
                    <ExportProductsButton supplierCompany={company} />
                    <Divider />
                  </>
                )}

                <CompanyContactInfoBox company={company} />

                <CompanyUsers company={company} withDivider />
              </Box>
            )}
            {contract?.status !== Contract_Status_Enum.Signed && displayProducts && (
              <Box
                sx={{
                  sm: {
                    py: 2,
                  },
                  mb: {
                    xs: '68px',
                    lg: 0,
                  },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography variant="titleLarge">
                    {productListTitle}
                  </Typography>
                  {switchGridList}
                </Box>
                <Box>
                  <CompanyProductSearch
                    contextCompanyId={companyId!}
                    companyId={productSearchCompanyId}
                    contract={contract}
                    searchInputRef={searchInputRef}
                  />
                  {isBuyer && <ProductRecommendations supplierId={companyId} />}
                </Box>
              </Box>
            )}
            {displayCompanyContacts && (
              <CompanyContacts companyId={companyId!} contract={contract} />
            )}
            {contract?.status !== Contract_Status_Enum.Signed && isSmallerThanLg && (
              <Box
                sx={{
                  position: 'fixed',
                  zIndex: 1000,
                  bottom: 0,
                  left: 0,
                  width: '100%',
                  p: 1.5,
                  boxSizing: 'border-box',
                  display: 'flex',
                  justifyContent: 'center',
                  backgroundColor: 'surface.main',
                  [`.${buttonClasses.root}`]: {
                    maxWidth: {
                      xs: 324,
                      lg: 'unset',
                    },
                    flex: 1,
                  },
                  '& > *': {
                    width: '100%',
                    justifyContent: 'center',
                  },
                }}
              >
                <ContactBox contactCompanyId={company.id} contract={contract} />
              </Box>
            )}
          </>
        )}
      </PageWrapper>
    </>
  );
}
